import { createSharedComposable } from '@vueuse/core'
import { useLocale } from '~/utils/locale'

// Notification data structures matching backend API
export interface NotificationInfo {
  type: 'TimeChanged' | 'TimeApproved' | 'TimeUnapproved' | 'CommentChanged' | 'ShiftAdded' | 'ShiftUpdated' | 'ShiftRemoved'
  id: number
  create_date: number
  is_read: boolean
  read_at?: number
  task_id: number
  day?: number
  start_date?: number
  end_date?: number
  user_id?: number
  user_first_name?: string
  user_last_name?: string
  comment?: string
  shift_date?: number
  shift_info?: string
}

export interface PaginationInfo {
  page: number
  per_page: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface NotificationListResponse {
  notifications: NotificationInfo[]
  pagination: PaginationInfo
}

export interface NotificationListRequest {
  page?: number
  per_page?: number
  filter_read?: boolean
  notification_type?: string
}

export interface MarkAsReadRequest {
  notification_id: number
  is_read: boolean
}

export interface UnreadCountResponse {
  unread_count: number
}

export interface WebPushPayload {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

// Legacy interface for backward compatibility
interface LegacyNotification {
  has_notification: boolean
  visited_notifications: Set<string>
}

function _useNotifications() {
  // Legacy cookie support
  const cookie = useCookie('notifications')
  
  // Reactive state
  const notifications = ref<NotificationInfo[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pagination = ref<PaginationInfo | null>(null)
  
  // Legacy states (for backward compatibility)
  const hasNotification = ref(false)
  const visitedNotifications = ref(new Set<string>())

  // Load legacy cookie data if available
  if (cookie.value) {
    try {
      hasNotification.value = cookie.value.has_notification
      visitedNotifications.value = new Set(cookie.value.visited_notifications)
    } catch (error) {
      console.warn('Failed to parse legacy notification cookie')
    }
  }

  // Watch legacy notification state
  watch(() => hasNotification.value, () => {
    saveLegacyCookie()
  })

  function saveLegacyCookie() {
    cookie.value = JSON.stringify({
      has_notification: hasNotification.value,
      visited_notifications: Array.from(visitedNotifications.value.values())
    })
  }

  // Legacy functions (for backward compatibility)
  function isVisited(id: number) {
    return visitedNotifications.value.has(id.toString())
  }

  function visited(id: number) {
    visitedNotifications.value.add(id.toString())
    saveLegacyCookie()
  }

  // API functions
  async function fetchNotifications(params: NotificationListRequest = {}) {
    loading.value = true
    error.value = null
    
    try {
      const query = new URLSearchParams()
      if (params.page) query.append('page', params.page.toString())
      if (params.per_page) query.append('per_page', params.per_page.toString())
      if (params.filter_read !== undefined) query.append('filter_read', params.filter_read.toString())
      if (params.notification_type) query.append('notification_type', params.notification_type)

      const response = await useNuxtApp().$customFetch<NotificationListResponse>(`/api/notification/list?${query.toString()}`)
      
      notifications.value = response.notifications
      pagination.value = response.pagination
      
      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch notifications'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function fetchUnreadCount() {
    try {
      const response = await useNuxtApp().$customFetch<UnreadCountResponse>('/api/notification/unread_count')
      unreadCount.value = response.unread_count
      return response.unread_count
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch unread count'
      throw err
    }
  }

  async function markAsRead(notificationId: number, isRead: boolean = true) {
    try {
      const payload: MarkAsReadRequest = {
        notification_id: notificationId,
        is_read: isRead
      }

      await useNuxtApp().$customFetch('/api/notification/mark_as_read', {
        method: 'POST',
        body: payload
      })

      // Update local state
      const notification = notifications.value.find(n => n.id === notificationId)
      if (notification) {
        notification.is_read = isRead
        if (isRead) {
          notification.read_at = Math.floor(Date.now() / 1000)
          if (unreadCount.value > 0) {
            unreadCount.value--
          }
        } else {
          notification.read_at = undefined
          unreadCount.value++
        }
      }

      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to mark notification as read'
      throw err
    }
  }

  async function markAllAsRead() {
    try {
      // Mark all current notifications as read
      const promises = notifications.value.map(notification => 
        markAsRead(notification.id, true)
      )
      
      await Promise.all(promises)
      
      // Update all notifications in local state
      notifications.value.forEach(notification => {
        notification.is_read = true
        notification.read_at = Math.floor(Date.now() / 1000)
      })
      
      unreadCount.value = 0
      
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to mark all notifications as read'
      throw err
    }
  }

  async function subscribeToWebPush(subscription: PushSubscription) {
    try {
      const p256dh = arrayBufferToBase64(subscription.getKey('p256dh'))
      const auth = arrayBufferToBase64(subscription.getKey('auth'))

      const payload: WebPushPayload = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh,
          auth
        }
      }

      await useNuxtApp().$customFetch('/api/notification/subscribe', {
        method: 'POST',
        body: payload
      })

      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to subscribe to push notifications'
      throw err
    }
  }

  // Helper function to convert ArrayBuffer to base64
  function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
    if (!buffer) return ''
    
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
  }

  // Format notification for display
  function formatNotification(notification: NotificationInfo): string {
    const { formatDate } = useLocale()
    const date = new Date(notification.create_date * 1000)
    const dateStr = formatDate(date)
    
    switch (notification.type) {
      case 'TimeChanged':
        return `${notification.user_first_name} ${notification.user_last_name} submitted time for approval (${dateStr})`
      case 'TimeApproved':
        return `Your time for ${dateStr} has been approved`
      case 'TimeUnapproved':
        return `Your time for ${dateStr} was not approved`
      case 'CommentChanged':
        return `Comment updated for ${dateStr}: "${notification.comment}"`
      default:
        return `Notification from ${dateStr}`
    }
  }

  // Get notification icon/color based on type
  function getNotificationStyle(notification: NotificationInfo) {
    // Console log for debugging
    console.log('Notification type:', notification.type)
    
    switch (notification.type) {
      case 'TimeChanged':
        return { icon: 'i-heroicons-clock', colorClass: 'text-blue-600' }
      case 'TimeApproved':
        return { icon: 'i-heroicons-check-badge', colorClass: 'text-green-600' }
      case 'TimeUnapproved':
        return { icon: 'i-heroicons-x-mark', colorClass: 'text-red-600' }
      case 'CommentChanged':
        return { icon: 'i-heroicons-chat-bubble-bottom-center-text', colorClass: 'text-orange-600' }
      case 'TaskAssigned':
        return { icon: 'i-heroicons-user-plus', colorClass: 'text-purple-600' }
      case 'TaskUpdated':
        return { icon: 'i-heroicons-pencil', colorClass: 'text-amber-600' }
      case 'TaskRemoved':
        return { icon: 'i-heroicons-trash', colorClass: 'text-red-500' }
      case 'ShiftAdded':
        return { icon: 'i-heroicons-plus', colorClass: 'text-emerald-600' }
      case 'ShiftUpdated':
        return { icon: 'i-heroicons-arrow-path', colorClass: 'text-blue-600' }
      case 'ShiftRemoved':
        return { icon: 'i-heroicons-minus', colorClass: 'text-red-500' }
      default:
        console.log('Using default icon for type:', notification.type)
        return { icon: 'i-heroicons-bell-alert', colorClass: 'text-gray-600' }
    }
  }

  // Auto-refresh functionality
  const refreshInterval = ref<NodeJS.Timeout | null>(null)

  function startAutoRefresh(intervalMs: number = 30000) {
    stopAutoRefresh()
    
    refreshInterval.value = setInterval(async () => {
      try {
        await fetchUnreadCount()
      } catch (err) {
        console.warn('Auto-refresh failed:', err)
      }
    }, intervalMs)
  }

  function stopAutoRefresh() {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
      refreshInterval.value = null
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopAutoRefresh()
  })

  return {
    // State
    notifications, // Remove readonly to allow mutations
    unreadCount: readonly(unreadCount),
    loading: readonly(loading),
    error: readonly(error),
    pagination: readonly(pagination),
    
    // API methods
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    subscribeToWebPush,
    
    // Utility methods
    formatNotification,
    getNotificationStyle,
    
    // Auto-refresh
    startAutoRefresh,
    stopAutoRefresh,
    
    // Legacy compatibility
    hasNotification: readonly(hasNotification),
    visitedNotifications: readonly(visitedNotifications),
    isVisited,
    visited
  }
}

export const useNotifications = createSharedComposable(_useNotifications)